import QRCode from 'qrcode'
import type { FiscalDiSn002SandboxValidateResult, FiscalInvoiceLinePrint, FiscalInvoicePrintData } from '@/types'
import { FBR_DIGITAL_INVOICING_LOGO_URL, FBR_QR_PRINT_PIXEL_SIZE } from '@/lib/fbrBranding'

function num(v: unknown, d = 0): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = parseFloat(v)
    return Number.isFinite(n) ? n : d
  }
  return d
}

function str(v: unknown): string {
  if (v == null) return ''
  return String(v).trim()
}

function extraTaxString(v: unknown): string {
  if (v == null) return ''
  if (typeof v === 'string') return v
  return String(v)
}

/** Parse FBR DI JSON returned from sandbox validate/post (same field names as production helpers). */
export function parseSn002FbrResponseBody(body: string): {
  dated?: string
  invoiceNumber?: string
  qrCode?: string
  lineInvoiceBySno: Record<string, string>
} {
  let root: Record<string, unknown>
  try {
    root = JSON.parse(body) as Record<string, unknown>
  } catch {
    return { lineInvoiceBySno: {} }
  }
  const lineInvoiceBySno: Record<string, string> = {}
  const dated = str(root.dated) || undefined
  let invoiceNumber = str(root.invoiceNumber)

  const qrKeys = ['qrCode', 'QRCode', 'QRCodeData', 'QrData', 'QR'] as const
  let qrCode = ''
  for (const k of qrKeys) {
    const q = str(root[k])
    if (q) {
      qrCode = q
      break
    }
  }

  const vr = root.validationResponse
  if (vr && typeof vr === 'object' && vr !== null) {
    const arr = (vr as { invoiceStatuses?: unknown }).invoiceStatuses
    if (Array.isArray(arr)) {
      for (const row of arr) {
        if (!row || typeof row !== 'object') continue
        const m = row as Record<string, unknown>
        const idx = str(m.itemSNo ?? m.ItemSNo)
        const ino = str(m.invoiceNo ?? m.InvoiceNo)
        if (idx && ino) lineInvoiceBySno[idx] = ino
      }
    }
  }

  const data = root.data
  if (data && typeof data === 'object' && data !== null) {
    const d = data as Record<string, unknown>
    if (!invoiceNumber) invoiceNumber = str(d.invoiceNumber ?? d.invoiceRefNo)
    if (!qrCode) {
      for (const k of qrKeys) {
        const q = str(d[k])
        if (q) {
          qrCode = q
          break
        }
      }
    }
  }

  return {
    dated,
    invoiceNumber: invoiceNumber || undefined,
    qrCode: qrCode || undefined,
    lineInvoiceBySno,
  }
}

/** Map the exact SN002 / DI request body (camelCase) to FiscalInvoicePrintData — no fabricated parties or lines. */
export function diRequestJsonToInvoicePrint(req: Record<string, unknown>, extras: { fbrPostedAt?: string; lineInv?: Record<string, string> }): FiscalInvoicePrintData | null {
  const items = req.items
  if (!Array.isArray(items) || items.length === 0) return null

  const lines: FiscalInvoiceLinePrint[] = []
  let sumExcl = 0
  let sumTax = 0
  let sumTot = 0

  items.forEach((raw, i) => {
    if (!raw || typeof raw !== 'object') return
    const it = raw as Record<string, unknown>
    const excl = num(it.valueSalesExcludingST ?? it.ValueSalesExcludingST)
    const tax = num(it.salesTaxApplicable ?? it.SalesTaxApplicable)
    const tot = num(it.totalValues ?? it.TotalValues)
    sumExcl += excl
    sumTax += tax
    sumTot += tot
    const sno = String(i + 1)
    const fbrNo = extras.lineInv?.[sno] ?? ''
    lines.push({
      item_s_no: i + 1,
      hs_code: str(it.hsCode ?? it.HSCode),
      product_description: str(it.productDescription ?? it.ProductDescription),
      uo_m: str(it.uoM ?? it.UoM ?? it.UOM),
      quantity: num(it.quantity ?? it.Quantity),
      value_sales_excluding_st: excl,
      rate: str(it.rate ?? it.Rate),
      sales_tax_applicable: tax,
      fixed_notified_value_or_retail_price: num(it.fixedNotifiedValueOrRetailPrice ?? it.FixedNotifiedValueOrRetailPrice),
      discount: num(it.discount ?? it.Discount),
      total_values: tot,
      sale_type: str(it.saleType ?? it.SaleType),
      sro_schedule_no: str(it.sroScheduleNo ?? it.SroScheduleNo),
      sro_item_serial_no: str(it.sroItemSerialNo ?? it.SroItemSerialNo),
      fed_payable: num(it.fedPayable ?? it.FedPayable),
      extra_tax: extraTaxString(it.extraTax ?? it.ExtraTax) || undefined,
      fbr_line_invoice_no: fbrNo || undefined,
    })
  })

  if (lines.length === 0) return null

  return {
    invoice_type: str(req.invoiceType ?? req.InvoiceType),
    invoice_date: str(req.invoiceDate ?? req.InvoiceDate),
    scenario_id: str(req.scenarioId ?? req.ScenarioId) || undefined,
    invoice_ref_no: str(req.invoiceRefNo ?? req.InvoiceRefNo) || undefined,
    fbr_posted_at: extras.fbrPostedAt,
    seller: {
      name: str(req.sellerBusinessName ?? req.SellerBusinessName),
      ntn_cnic: str(req.sellerNTNCNIC ?? req.SellerNTNCNIC),
      province: str(req.sellerProvince ?? req.SellerProvince),
      address: str(req.sellerAddress ?? req.SellerAddress),
    },
    buyer: {
      registration_type: str(req.buyerRegistrationType ?? req.BuyerRegistrationType),
      name: str(req.buyerBusinessName ?? req.BuyerBusinessName),
      ntn_cnic: str(req.buyerNTNCNIC ?? req.BuyerNTNCNIC),
      province: str(req.buyerProvince ?? req.BuyerProvince),
      address: str(req.buyerAddress ?? req.BuyerAddress),
    },
    lines,
    totals_value_excl_st: Math.round(sumExcl * 100) / 100,
    totals_sales_tax: Math.round(sumTax * 100) / 100,
    totals_grand: Math.round(sumTot * 100) / 100,
  }
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function money(n: number): string {
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function ob(l: string, v: string): string {
  return `<div class="fbr-tr-ob"><span>${esc(l)}</span><span>${esc(v)}</span></div>`
}

async function fbrLogoDataUrl(): Promise<string> {
  try {
    const u = /^https?:\/\//i.test(FBR_DIGITAL_INVOICING_LOGO_URL)
      ? FBR_DIGITAL_INVOICING_LOGO_URL
      : `${window.location.origin}${FBR_DIGITAL_INVOICING_LOGO_URL}`
    const res = await fetch(u)
    const blob = await res.blob()
    return await new Promise<string>((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = () => resolve(FBR_DIGITAL_INVOICING_LOGO_URL)
      reader.readAsDataURL(blob)
    })
  } catch {
    return FBR_DIGITAL_INVOICING_LOGO_URL
  }
}

/**
 * Build a standalone HTML document for the SN002 validate run using only the outbound request body,
 * the FBR response body, and optional venue logo from receipt settings (same sources as the live test).
 */
export async function buildSn002TestReceiptHtml(
  result: FiscalDiSn002SandboxValidateResult,
  venueLogoUrl?: string | null,
  venueLogoWidthPct?: number,
): Promise<string | null> {
  const rawReq = (result.request_preview ?? '').trim()
  if (!rawReq) return null
  let req: Record<string, unknown>
  try {
    req = JSON.parse(rawReq) as Record<string, unknown>
  } catch {
    return null
  }

  const parsed =
    result.response_body && result.response_body.trim()
      ? parseSn002FbrResponseBody(result.response_body)
      : { lineInvoiceBySno: {} as Record<string, string> }

  const dated = parsed.dated
  const invFromResp = parsed.invoiceNumber || str(result.response_invoice_number)
  const extras = {
    fbrPostedAt: dated,
    lineInv: parsed.lineInvoiceBySno,
  }
  const invoice = diRequestJsonToInvoicePrint(req, extras)
  if (!invoice) return null

  const qrPayload = (parsed.qrCode || invFromResp || '').trim()
  if (!qrPayload) return null

  const irn = invFromResp || qrPayload

  const [qrDataUrl, fbrMarkSrc] = await Promise.all([
    QRCode.toDataURL(qrPayload, {
      width: FBR_QR_PRINT_PIXEL_SIZE,
      margin: 2,
      errorCorrectionLevel: 'M',
    }),
    fbrLogoDataUrl(),
  ])

  const venue =
    venueLogoUrl && /^(https?:\/\/|data:image\/)/i.test(venueLogoUrl)
      ? `<div class="fbr-tr-venue"><img src="${esc(venueLogoUrl)}" alt="" style="max-width:${Math.round(venueLogoWidthPct ?? 75)}%;max-height:48px;object-fit:contain" /></div>`
      : ''

  const lineRows = invoice.lines
    .map((ln) => {
      const cells = [
        esc(ln.product_description || '—'),
        esc(ln.hs_code || '—'),
        esc(ln.uo_m || '—'),
        String(ln.quantity ?? ''),
        money(ln.value_sales_excluding_st ?? 0),
        esc(ln.rate || '—'),
        money(ln.sales_tax_applicable ?? 0),
        money(ln.total_values ?? 0),
        esc(ln.sale_type || ''),
        esc(ln.fbr_line_invoice_no || '—'),
      ]
      return `<tr>${cells.map((c) => `<td>${c}</td>`).join('')}</tr>`
    })
    .join('')

  const subParts = [
    invoice.invoice_ref_no ? `Ref ${invoice.invoice_ref_no}` : null,
    invoice.invoice_date ? `Date ${invoice.invoice_date}` : null,
    'SN002 sandbox validate',
  ].filter(Boolean)

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>FBR test receipt — SN002</title>
  <style>
    body { font-family: system-ui, Segoe UI, Arial, sans-serif; margin: 16px; color: #111; }
    .fbr-tr-wrap { max-width: 420px; margin: 0 auto; border: 1px solid #ccc; border-radius: 12px; overflow: hidden; background: #fff; }
    .fbr-tr-head { text-align: center; padding: 12px 16px; background: #f4f4f5; border-bottom: 1px solid #e4e4e7; }
    .fbr-tr-fbr-logo img { height: 56px; width: auto; display: inline-block; }
    .fbr-tr-venue { margin-top: 8px; }
    .fbr-tr-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #52525b; margin-top: 8px; }
    .fbr-tr-sub { font-size: 10px; color: #71717a; margin-top: 4px; line-height: 1.35; }
    .fbr-tr-body { padding: 14px 16px; font-size: 11px; line-height: 1.35; }
    .fbr-tr-sec-title { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .05em; color: #71717a; border-bottom: 1px solid #e4e4e7; padding-bottom: 4px; margin: 12px 0 8px; }
    .fbr-tr-ob { display: flex; justify-content: space-between; gap: 8px; margin: 2px 0; }
    .fbr-tr-ob span:first-child { color: #52525b; flex-shrink: 0; }
    .fbr-tr-ob span:last-child { font-weight: 600; text-align: right; word-break: break-word; }
    .fbr-tr-multiline { white-space: pre-wrap; margin-top: 4px; }
    .fbr-tr-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 8px; margin-top: 8px; }
    table.fbr-tr-lines { width: 100%; border-collapse: collapse; font-size: 9px; margin-top: 8px; }
    .fbr-tr-lines th, .fbr-tr-lines td { border: 1px solid #e4e4e7; padding: 3px 4px; vertical-align: top; }
    .fbr-tr-lines th { background: #f4f4f5; text-align: left; }
    .fbr-tr-tot { display: flex; justify-content: space-between; font-weight: 800; margin-top: 10px; padding-top: 8px; border-top: 1px solid #e4e4e7; }
    .fbr-tr-foot { text-align: center; margin-top: 14px; padding-top: 12px; border-top: 1px solid #e4e4e7; }
    /* FBR spec: verification QR printed at 1″ × 1″ */
    .fbr-tr-qr img { width: 1in; height: 1in; display: inline-block; image-rendering: pixelated; border: 1px solid #ddd; padding: 2px; background: #fff; }
    .fbr-tr-irn-l { font-size: 10px; text-transform: uppercase; letter-spacing: .06em; color: #71717a; }
    .fbr-tr-irn-v { font-family: ui-monospace, monospace; font-size: 11px; font-weight: 700; word-break: break-all; margin: 6px 0; }
    .fbr-tr-hint { font-size: 9px; color: #71717a; }
    @media print { body { margin: 0; } .fbr-tr-wrap { border: none; max-width: none; } }
  </style>
</head>
<body>
  <div class="fbr-tr-wrap">
    <div class="fbr-tr-head">
      <div class="fbr-tr-fbr-logo"><img src="${esc(fbrMarkSrc)}" alt="FBR POS Invoicing System"/></div>
      ${venue}
      <div class="fbr-tr-title">Digital tax invoice (FBR)</div>
      <div class="fbr-tr-sub">${subParts.map((p) => esc(String(p))).join(' · ')}</div>
    </div>
    <div class="fbr-tr-body">
      <div class="fbr-tr-sec-title">Seller</div>
      ${ob('Business', invoice.seller.name || '—')}
      ${ob('STRN / NTN CNIC', invoice.seller.ntn_cnic || '—')}
      ${ob('Province', invoice.seller.province || '—')}
      <div class="fbr-tr-multiline">${esc(invoice.seller.address || '')}</div>

      <div class="fbr-tr-sec-title">Buyer</div>
      ${ob('Type', invoice.buyer.registration_type || '—')}
      ${ob('Name', invoice.buyer.name || '—')}
      ${ob('NTN / CNIC', invoice.buyer.ntn_cnic || '—')}
      ${ob('Province', invoice.buyer.province || '—')}
      <div class="fbr-tr-multiline">${esc(invoice.buyer.address || '')}</div>

      <div class="fbr-tr-meta">
        ${ob('Invoice type', invoice.invoice_type || '—')}
        ${ob('Date', invoice.invoice_date || '—')}
        ${ob('Scenario', invoice.scenario_id || '—')}
        ${invoice.fbr_posted_at ? ob('FBR dated', invoice.fbr_posted_at) : ''}
      </div>

      <table class="fbr-tr-lines">
        <thead><tr>
          <th>Desc</th><th>HS</th><th>UoM</th><th>Qty</th><th>Ex-ST</th><th>Rate</th><th>ST</th><th>Tot</th><th>Sale type</th><th>FBR #</th>
        </tr></thead>
        <tbody>${lineRows}</tbody>
      </table>

      <div class="fbr-tr-tot"><span>Grand total</span><span>${money(invoice.totals_grand)} PKR</span></div>

      <div class="fbr-tr-foot">
        <div class="fbr-tr-qr"><img src="${esc(qrDataUrl)}" width="${FBR_QR_PRINT_PIXEL_SIZE}" height="${FBR_QR_PRINT_PIXEL_SIZE}" alt="Verification QR"/></div>
        <div class="fbr-tr-irn-l">FBR invoice / IRN</div>
        <div class="fbr-tr-irn-v">${esc(irn)}</div>
        <div class="fbr-tr-hint">Generated from your saved fiscal settings and the SN002 validate request/response (sandbox). Verify via FBR or authorized channels.</div>
      </div>
    </div>
  </div>
</body>
</html>`
}

export async function downloadSn002TestReceipt(
  result: FiscalDiSn002SandboxValidateResult,
  venueLogoUrl?: string | null,
  venueLogoWidthPct?: number,
): Promise<boolean> {
  const html = await buildSn002TestReceiptHtml(result, venueLogoUrl, venueLogoWidthPct)
  if (!html) return false
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const u = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = u
  a.download = 'chaikhana-fbr-sn002-test-receipt.html'
  a.click()
  URL.revokeObjectURL(u)
  return true
}
